/**
 * The RVTM as a CSV file: the matrix the screen draws, in words.
 *
 * WHY IT IS BUILT HERE. The cell states are worked out in the browser by
 * `classifyCell`, and a server copy of that rule would be a second
 * implementation of it (issue #100). So the file is made from the same
 * `MatrixRow`s the grid draws, and only the screen can produce it.
 *
 * The file mirrors the page. Each method column says what the screen's legend
 * says, in the legend's own words (`CELL_LABEL`), followed by the cases that
 * cover it. A requirement with no method stated, and one with no case at all,
 * are different things here too, and so they are different columns.
 *
 * Same file rules as the server's RTM CSV: UTF-8 with no byte-order mark, LF
 * line endings, and quoting only where a field needs it.
 */

import type { CellKind, MatrixCell, MatrixRow } from './VerificationTraceability'
import { NONE } from '../components/entity/quickFilter'
import type { QuickSelection } from '../components/entity/quickFilter'

/**
 * What each cell state says, in words.
 *
 * The screen's legend and the file read from here, so the two cannot drift.
 */
export const CELL_LABEL: Record<CellKind, string> = {
  covered: 'nominated and covered',
  gap: 'nominated, no case uses it',
  mismatch: 'a case uses it, the requirement nominates others',
  unattributed: 'a case uses it, the requirement nominates nothing',
  none: 'not nominated, no case',
}

/**
 * The caveat the screen gives when the verification listing was capped.
 *
 * `read` is the entries fetched and `total` the entries the server holds. An
 * entry beyond the cap is unread, so a case in it reads as stating no method and
 * coverage is understated. Shared with the screen's warning so the file and the
 * page say the same thing.
 */
export function coverageCaveat(read: number, total: number): string {
  return (
    `Read the first ${read} of ${total} entries. ` +
    'An entry beyond those reads as stating no method, which understates coverage.'
  )
}

/**
 * One CSV field, quoted only where it needs to be (RFC 4180, as Python's csv
 * writes it for the server's RTM file).
 */
function field(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value
}

/** One CSV line, LF-terminated. */
function line(values: string[]): string {
  return `${values.map(field).join(',')}\n`
}

/**
 * What one method cell says: its state in words, then the cases behind it.
 *
 * A cell in the 'none' state is blank, the same as the screen draws it.
 */
function cellText(cell: MatrixCell | undefined): string {
  if (!cell || cell.kind === 'none') return ''
  const words = CELL_LABEL[cell.kind]
  return cell.cases.length > 0 ? `${words}: ${cell.cases.join(', ')}` : words
}

/**
 * The matrix as CSV.
 *
 * Columns: the requirement, its level and subsystem, the methods it nominates,
 * one column per method, then "No method stated" (the cases that state none)
 * and "No case" (filled only when nothing verifies the requirement).
 *
 * @param rows the rows to write, in the order to write them
 * @param methods the method columns, in the screen's order
 * @param caveat a line written above the header, or null when there is none
 */
export function matrixCsv(rows: MatrixRow[], methods: string[], caveat: string | null): string {
  const header = [
    'Requirement',
    'Level',
    'Subsystem',
    'Nominated methods',
    ...methods,
    'No method stated',
    'No case',
  ]
  const lines = caveat ? [line([caveat])] : []
  lines.push(line(header))

  for (const row of rows) {
    lines.push(
      line([
        row.id,
        row.level,
        row.subsystem,
        row.nominated.join(', '),
        ...methods.map((method) => cellText(row.cells.find((cell) => cell.method === method))),
        row.unstated.join(', '),
        row.cases.length === 0 ? 'no case' : '',
      ]),
    )
  }
  return lines.join('')
}

/**
 * The filter fields that name a file: subsystem first, the way the everyday
 * slice reads ("PROP at L3 and L4"), then level and segment.
 */
const NAMING_FIELDS = ['subsystem', 'level', 'segment'] as const

/**
 * The file name for a narrowed matrix: `rvtm-PROP-L3-L4.csv` for subsystem
 * PROP at levels 3 and 4, so a slice is never mistaken for the whole program.
 *
 * The chosen values, field by field in NAMING_FIELDS order, with the blank
 * choice named 'none'. A narrowing with no quick filter chosen (a search, or a
 * Show choice) is named 'filtered'. Anything that is not a plain letter, digit,
 * underscore, dot or hyphen becomes an underscore, so the name is safe to save.
 *
 * @param selection the quick filters chosen
 * @param narrowed whether the screen is narrowed at all
 */
export function matrixFileName(selection: QuickSelection, narrowed: boolean): string {
  const parts = NAMING_FIELDS.flatMap((name) =>
    (selection[name] ?? []).map((value) => (value === NONE ? 'none' : value)),
  ).map((part) => part.replace(/[^A-Za-z0-9_.-]+/g, '_'))

  const label = parts.length > 0 ? parts.join('-') : narrowed ? 'filtered' : ''
  return label ? `rvtm-${label}.csv` : 'rvtm.csv'
}
